"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DappRegistry = exports.RatingsContract = exports.DappRegistryContract = void 0;
const ethers_1 = require("ethers");
const constants_1 = require("../constants");
const DappRegistry_json_1 = __importDefault(require("../contracts/DappRegistry/DappRegistry.json"));
const Ratings_json_1 = __importDefault(require("../contracts/Ratings/Ratings.json"));
const model_1 = require("../model");
const tx_1 = require("../utils/tx");
exports.DappRegistryContract = DappRegistry_json_1.default;
exports.RatingsContract = Ratings_json_1.default;
const ADMIN_ROLE = ethers_1.utils.hexZeroPad(ethers_1.utils.hexlify(0), 32);
const VALIDATOR_ROLE = ethers_1.utils.keccak256(ethers_1.utils.toUtf8Bytes('VALIDATOR_ROLE'));
/**
 * DappRegistry Class
 * Provides interface for interaction with the DappRegistry smart contracts
 */
class DappRegistry {
    constructor(config = constants_1.DAPP_REGISTRY_ENVIRONMENT_CONFIGS[model_1.Environments.LOCALHOST], signerOrProvider = null, connectionConfig) {
        this._provider = new ethers_1.providers.JsonRpcProvider(connectionConfig || config.rpcUrl);
        this._dappRegistryContract = new ethers_1.Contract(config.dappRegistryAddress, exports.DappRegistryContract.abi, this._provider);
        this._ratingsContract = new ethers_1.Contract(config.ratingsAddress, exports.RatingsContract.abi, this._provider);
        if (signerOrProvider) {
            this.connect(signerOrProvider);
        }
    }
    /**
     * returns RPC provider
     */
    get provider() {
        return this._provider;
    }
    /**
     * Connects signer to the smart contract
     * @param signerOrProvider An instance of ethers.js Wallet or any other signer
     */
    connect(signerOrProvider) {
        this._dappRegistryContract = this._dappRegistryContract.connect(signerOrProvider);
        this._ratingsContract = this._ratingsContract.connect(signerOrProvider);
    }
    grantAdminRole(address) {
        return (0, tx_1.waitTransaction)(this._dappRegistryContract.grantRole(ADMIN_ROLE, address));
    }
    isAdmin(address) {
        return this._dappRegistryContract.hasRole(ADMIN_ROLE, address);
    }
    revokeAdminRole(address) {
        return (0, tx_1.waitTransaction)(this._dappRegistryContract.revokeRole(ADMIN_ROLE, address));
    }
    grantValidatorRole(address) {
        return (0, tx_1.waitTransaction)(this._dappRegistryContract.grantRole(VALIDATOR_ROLE, address));
    }
    isValidator(address) {
        return this._dappRegistryContract.hasRole(VALIDATOR_ROLE, address);
    }
    revokeValidatorRole(address) {
        return (0, tx_1.waitTransaction)(this._dappRegistryContract.revokeRole(VALIDATOR_ROLE, address));
    }
    createRecord(location, urlHash) {
        return (0, tx_1.waitTransaction)(this._dappRegistryContract.createRecord(location, urlHash));
    }
    editRecord(recordHash, newLocation) {
        return (0, tx_1.waitTransaction)(this._dappRegistryContract.editRecord(recordHash, newLocation));
    }
    validateRecord(recordHash) {
        return (0, tx_1.waitTransaction)(this._dappRegistryContract.validateRecord(recordHash));
    }
    unvalidateRecord(recordHash) {
        return (0, tx_1.waitTransaction)(this._dappRegistryContract.unvalidateRecord(recordHash));
    }
    getValidatedRecords(address) {
        return this._dappRegistryContract.getValidatedRecords(address);
    }
    getRecordCount() {
        return this._dappRegistryContract.getRecordCount();
    }
    getRecordSlice(start, length) {
        return this._dappRegistryContract.getRecordSlice(start, length);
    }
    getRecord(recordHash) {
        return __awaiter(this, void 0, void 0, function* () {
            const record = yield this._dappRegistryContract.getRecord(recordHash);
            return this.convertDappRecord(record);
        });
    }
    getRecords(recordHashes) {
        return __awaiter(this, void 0, void 0, function* () {
            const records = yield this._dappRegistryContract.getRecords(recordHashes);
            return records.map(record => this.convertDappRecord(record));
        });
    }
    getUserRecordHashes(address) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._dappRegistryContract.getUserRecordHashes(address);
        });
    }
    rateDapp(recordLocation, review, rating) {
        return (0, tx_1.waitTransaction)(this._ratingsContract.rate(recordLocation, review, rating));
    }
    getAverageRating(recordLocation) {
        return __awaiter(this, void 0, void 0, function* () {
            const averageRating = Number(yield this._ratingsContract.getAverageRating(recordLocation));
            return Number.isInteger(averageRating) ? averageRating : 0;
        });
    }
    getNumberOfRatings(recordLocation) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._ratingsContract.getNumberOfRatings(recordLocation);
        });
    }
    hasUserRated(recordLocation) {
        return __awaiter(this, void 0, void 0, function* () {
            return Boolean(yield this._ratingsContract.hasUserRated(recordLocation));
        });
    }
    getRatingFor(recordLocation) {
        return __awaiter(this, void 0, void 0, function* () {
            const records = yield this._ratingsContract.getRatingFor(recordLocation);
            return records.map((record) => this.convertRatingRecord(record));
        });
    }
    convertDappRecord(record) {
        let timestamp = new Date('');
        try {
            timestamp = new Date(record[7].toNumber());
        }
        catch (error) { }
        return {
            recordHash: record[0],
            creator: record[1],
            location: record[2],
            urlHash: record[3],
            edited: Boolean(record[4]),
            index: ethers_1.BigNumber.from(record[5]),
            creatorIndex: ethers_1.BigNumber.from(record[6]),
            timestamp,
        };
    }
    convertRatingRecord(record) {
        return {
            rating: Number(record[0]),
            review: record[1],
            user: record[2],
        };
    }
}
exports.DappRegistry = DappRegistry;
